import { Alert, AlertColor, Box, Checkbox, CircularProgress, FormControl, FormControlLabel, FormGroup, Grid, InputLabel, MenuItem, Radio, RadioGroup, Select, Typography } from "@mui/material";
import { FC, useEffect, useState } from "react";
import { useCreateViewMutation, useListRegionsQuery } from "../../generated";
import LoadingButton from "@mui/lab/LoadingButton";
import { useCreateIndex } from "../../hooks/useCreateIndex";
import SixPillarsRoleButton from "../common/SixPillarsRoleButton";
import useAccount from "../../hooks/useAccount";
import useListRegions from "../../hooks/useListRegions";

interface AlertMsg {
  severity: AlertColor;
  msg: string;
}

interface CreateIndexProps {
	arn: string;
	region: string;
	setRegion: (index: string) => void;
  data: any;
  loading: boolean;
  refetch: any;
  setAlerts: (alerts: AlertMsg[]) => void
  changeCreateTab: (
    index: number,
  ) => void;
  changeTab: (
    index: number,
  ) => void;
}

const CreateIndex: FC<CreateIndexProps> = ({
  arn,
	region,
	setRegion,
  data,
  loading,
  refetch,
  setAlerts,
  changeCreateTab,
  changeTab,
}: CreateIndexProps) => {
  const { accounttype, defaultregion, externalId } = useAccount();
  const regions = useListRegions();
  const [value, setValue] = useState('all-regions');
  const [viewValue, setViewValue] = useState('default-view');
  const [indexCreating, setIndexCreating] = useState(false);
	const [activeSpinner, setActiveSpinner] = useState(false); // spinner that displays while view is being created
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [availableRegions, setAvailableRegions] = useState<string[]>([]);

  const { data: listRegionsData, loading: listRegionsLoading } = useListRegionsQuery({
    variables: {
      arn, // value for 'arn'
    },
    context: {
      apiName: "resource_explorer"
    },
  });
  const { createIndex } = useCreateIndex();
  const [createViewMutation] = useCreateViewMutation();

  // Handle Displayed Regions Checkbox
  const handleRegionCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>, regionName: string) => {
    const isChecked = e.target.checked;
  
    // Skip if the region is already indexed
    const isIndexed = data?.listIndexes?.items?.some((item: any) => item.Region === regionName);
    if (isIndexed) return;
  
    setSelectedRegions((prev) =>
      isChecked ? [...prev, regionName] : prev.filter((r) => r !== regionName)
    );
  };

  useEffect(() => {
    if (listRegionsData?.listRegions?.items && data?.listIndexes?.items) {
      const indexedRegions = data.listIndexes.items.map((item: any) => item.Region);
      const allRegions = listRegionsData.listRegions.items.map((item: any) => item.RegionName);
      const unindexedRegions = allRegions.filter((region: string) => !indexedRegions.includes(region));
      
      setAvailableRegions(unindexedRegions); // these are the ones we render
      setSelectedRegions(unindexedRegions);  // optionally check them all by default
    }

    if (!loading && data?.listIndexes?.items?.length !== 0) {
      setViewValue("manual-view");
    }
  }, [listRegionsData, data]);
  
  // Handle Create Indexes
  const submitOnClick = async () => {
    setActiveSpinner(true);
    const response = await createIndex(
      arn, // value for 'arn'
      selectedRegions, // value for 'regionList'
      region // value for 'aggregatorRegion'
    );
    if (response?.indexcreation === true){
      setAlerts([
        {
          severity: "success",
          msg: "Resource Explorer Index Successfully Created.",
        },
      ]);
      setIndexCreating(true);
      if (viewValue === "default-view") {
        const response = await createViewMutation({
          variables: {
            arn, // value for 'arn'
            ViewName: "default", // value for 'ViewName'
            FilterString: "", // value for 'FilterString'
            tags: true,// value for 'tags'
            region, // value for 'region'
          },
          context: {
            apiName: "resource_explorer"
          },
        });
        if (response?.data?.createView?.status === "COMPLETE"){
          setAlerts([
            {
              severity: "success",
              msg: `Default view created successfully!`,
            },
          ]);
          changeTab(1);
        } else if (response?.data?.createView?.status === "ERROR") {
          setAlerts([
            {
              severity: "error",
              msg: "Failed to create a view!",
            },
          ]);
          changeCreateTab(1);
        }
      } else {
        // refetch create index data to get new results after index has been created
        refetch();
        // Switch to create view tab since customer didn't create a default view
        changeCreateTab(1);
      }
			setActiveSpinner(false);
    }
  };

  useEffect(() => {
    //Call list indexes Query again to check if alert is valid
    if(indexCreating){
      refetch();
    }
  }, [indexCreating]);
  
  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedValue = (event.target as HTMLInputElement).value;
    setValue(selectedValue);
  };

  // Handle if customer creates a default view or creates a manual one later
  const handleViewChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedView = (event.target as HTMLInputElement).value;
    setViewValue(selectedView);
  };

  //Handle Region Select
	const handleRegionSelect = (e: any) => {
		setRegion(e.target.value);
	};
  
  return (
    <Grid
      container
      sx={{ pb: 2, pt: 2, pl: 2 }}
      spacing={2}
      alignItems="stretch"
      flexDirection={"column"}
    >
      {data?.listIndexes == null ? (
        <Grid item md={12}>
          {accounttype === "AUTOMATED" 
            ? 
            (
              <Alert variant="outlined" severity="error">
                <Typography sx={{ fontSize: "12px" }}>
                  We couldn't find a suitable role in AWS Account {arn.split(":")[4]+" "} 
                  with the necessary permissions to enable resource tagging.
                  To update your existing role, please follow these
                  instructions.
                </Typography>
                <Typography sx={{ fontSize: "12px" }}>
                  Step 1: Click the "Open CloudFormation" button below to open
                  CloudFormation in your AWS account
                </Typography>
                <Typography sx={{ fontSize: "12px" }}>
                  Step 2: Select the "six-pillars" stack
                </Typography>
                <Typography sx={{ fontSize: "12px" }}>
                  Step 3: Click "Update"
                </Typography>
                <Typography sx={{ fontSize: "12px" }}>
                  Step 4: Select "Replace Existing Template"
                </Typography>
                <Typography sx={{ fontSize: "12px" }}>
                  Step 5: In AUTOMATE+, click on the "Copy Role Stack URL"
                  button
                </Typography>
                <Typography sx={{ fontSize: "12px" }}>
                  Step 6: In the AWS Console, paste the Role Stack URL into
                  the "Amazon S3 URL" field and click "Next" until you can
                  select "Submit"
                </Typography>
                <SixPillarsRoleButton
                  launch={false}
                  update={true}
                  open={true}
                  uuid={externalId}
                  accountRegion={defaultregion}
                  template={
                    "roles/SixPillarRoleStack.template.json"
                  }
                ></SixPillarsRoleButton>
              </Alert>
            )
            :
            (
              <Alert variant="outlined" severity="error">
                <Typography sx={{ fontSize: "12px" }}>
                  AWS Account {arn.split(":")[4]} has to be AUTOMATE+ to perform resource tagging. 
                  Please rollback your current account and redeploy 6pillars with AUTOMATE+ deployment and ensure that the resource tagging role is selected in the Six Pillars CloudFormation template.
                </Typography>
              </Alert>
            )
          }
        </Grid>
      ):(
        <>
        {data?.listIndexes?.items.length === 0 ? (
          <>
          <Grid item xs md={12}>
            <Typography
              component={"span"}
              variant="h6"
              color="text.primary"
              sx={{ mb: 1.5 }}
              paragraph
            >
              Turn on Resource Explorer.
            </Typography>
          </Grid>
          <Grid item xs md={12}>
            <Typography
              component={"span"}
              variant="body1"
              color="text.primary"
              sx={{ mb: 1.5 }}
              paragraph
            >
              Before you can search for resources in your AWS account, you must turn on Resource Explorer and let it build an index of your resources.
            </Typography>
          </Grid>
          <Grid item md={12}>
            <Alert variant="outlined" severity="warning">
              AWS Account {arn.split(":")[4]} doesn't have resource explorer turned on.
              Please follow the process below to create the Index.
            </Alert>
          </Grid>
          <Grid item xs md={12}>
            <Typography
              component={"span"}
              variant="subtitle2"
              color="text.primary"
              paragraph
            >
              Regions
            </Typography>
            <FormGroup>
              <RadioGroup
                aria-labelledby="resource-explorer-regions-radio-buttons-group"
                name="resource-explorer-regions-radio-buttons-group"
                value={value}
                onChange={handleFilterChange}
              >
                <FormControlLabel value="all-regions" control={<Radio />} label="Create indexes in all AWS Regions in this account." />
                <FormControlLabel value="selected-regions" control={<Radio />} label="Create indexes in only the selected AWS Regions in this account." />
              </RadioGroup>
            </FormGroup>
          </Grid>
          {value === "selected-regions" ? (
            <Grid container sx={{ pl: 4 }}>
              {[...availableRegions].sort().map((regionName) => (
                <Grid item xs={12} sm={6} md={4} key={regionName}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedRegions.includes(regionName)}
                        onChange={(e) => handleRegionCheckboxChange(e, regionName)}
                      />
                    }
                    label={regions.get(regionName)}
                  />
                </Grid>
              ))}
            </Grid>
          ) : null}
          <Grid item md={12}>
            <Typography
              component={"span"}
              variant="subtitle2"
              color="text.primary"
              paragraph
            >
              Aggregator index Region
            </Typography>
          </Grid>
          <Grid item md={12}>
            <Typography
              component={"span"}
              variant="caption"
              color="text.primary"
              paragraph
              gutterBottom
            >
              Choose the AWS Region to contain the aggregator index for all Regions in this AWS account. You can move the aggregator index later by deleting and recreating it in a different Region.
            </Typography>
          </Grid>
          <Grid item md={6}>
            <Box sx={{ minWidth: 120 }}>
              <FormControl fullWidth>
                <InputLabel id="resource-explorer-aggregator-index-region">
                  Aggregator index Region
                </InputLabel>
                <Select
                  required
                  id="resource-explorer-aggregator-index-region-select"
                  name="Aggregator index Region"
                  value={selectedRegions.includes(region) ? region : ""}
                  label="Aggregator index Region"
                  onChange={handleRegionSelect}
                >
                  {[...selectedRegions].sort().map((regionName) => (
                    <MenuItem key={regionName} value={regionName}>
                      {regions.get(regionName)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Grid>
          <Grid item md={6}></Grid>
          <Grid item xs md={12}>
            <Typography
              component={"span"}
              variant="h6"
              color="text.primary"
              sx={{ mb: 1.5 }}
              paragraph
            >
              Default view
            </Typography>
          </Grid>
          <Grid item md={12}>
            <Typography
              component={"span"}
              variant="caption"
              color="text.primary"
              paragraph
              gutterBottom
            >
              A view is a set of filters that you use to access the resource information in an AWS Resource Explorer index. You can create additional views that filter the information in different ways.
            </Typography>
          </Grid>
          <Grid item xs md={12}>
            <Typography
              component={"span"}
              variant="subtitle2"
              color="text.primary"
              paragraph
            >
              Default view creation
            </Typography>
            <FormGroup>
              <RadioGroup
                aria-labelledby="resource-explorer-view-radio-buttons-group"
                name="resource-explorer-view-radio-buttons-group"
                value={viewValue}
                onChange={handleViewChange}
              >
                <FormControlLabel
                  value="default-view"
                  control={<Radio />}
                  label="Create a default view with filters that include all resources in the aggregator AWS Region."
                />
                <FormControlLabel
                  value="manual-view"
                  control={<Radio />}
                  label="Do not create a default view. You must manually create at least one view later for users to search."
                />
              </RadioGroup>
            </FormGroup>
          </Grid>
          </>
        ):(
          <>
          <Grid item xs md={12}>
            <Typography
              component={"span"}
              variant="h6"
              color="text.primary"
              sx={{ mb: 1.5 }}
              paragraph
            >
              Create Index in Resource Explorer.
            </Typography>
          </Grid>
          <Grid item md={12}>
            <Typography
              component={"span"}
              variant="body1"
              color="text.primary"
              sx={{ mb: 1.5 }}
              paragraph
            >
              Select one or more AWS Regions in which you want to create indexes. You can search for resources only in Regions that have indexes.
            </Typography>
          </Grid>
          <Grid container sx={{ pl: 4 }}>
            {[...availableRegions].sort().map((regionName) => (
              <Grid item xs={12} sm={6} md={4} key={regionName}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedRegions.includes(regionName)}
                      onChange={(e) => handleRegionCheckboxChange(e, regionName)}
                    />
                  }
                  label={regions.get(regionName)}
                />
              </Grid>
            ))}
          </Grid>
          </>
        )}
        <Grid item xs md={4} justifyContent="flex-end">
          <LoadingButton
            id="resource-explorer-create-btn"
            variant="contained"
            color="secondary"
            disabled={loading || listRegionsLoading || activeSpinner}
            loading={loading || listRegionsLoading || activeSpinner}
            onClick={() => 
              submitOnClick()
            }
            sx={{
              width: 157,
              height: 40,
              bgcolor: "secondary.main",
              "&:hover": {
                bgcolor: "secondary.main",
                color: "secondary.contrastText",
              },
            }}
            loadingIndicator={
              <CircularProgress color={"secondary"} size={22} />
            }
          >
            Create
          </LoadingButton>
        </Grid> 
        </>
      )}
    </Grid>
  )
};

export default CreateIndex;